import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Drawer,
  IconButton,
  Box,
  Chip,
  useMediaQuery,
  Divider,
  Tabs,
  Tab,
  Modal,
} from "@mui/material";
import "./Work.css";
import * as THREE from "three";

import CloseIcon from "@mui/icons-material/Close";

import contract_gif from "../src/assets/contracts/contract_gif.gif";
import contract_gif2 from "../src/assets/contracts/contract_gif2.gif";
import contract_gif3 from "../src/assets/contracts/contract_gif3.gif";
import contract_gif4 from "../src/assets/contracts/contract_4.png";
import contract_gif5 from "../src/assets/contracts/contract_gif5.gif";

import nyshex_image from "../src/assets/nyshex/nyshex.webp";
import nyshex_image2 from "../src/assets/nyshex/nyshex2.webp";
import nyshex_image3 from "../src/assets/nyshex/nyshex3.webp";
import nyshex_image4 from "../src/assets/nyshex/nyshex4.webp";

import actionface_image2 from "../src/assets/actionface/actionface2.gif";
import actionface_image from "../src/assets/actionface/actionface.jpg";

import intel_gif from "../src/assets/intel/intel_vid.gif";
import intel_gif2 from "../src/assets/intel/intel2.png";
import intel_gif3 from "../src/assets/intel/intel3.jpg";
import intel_gif4 from "../src/assets/intel/intel4.jpg";
import intel_gif5 from "../src/assets/intel/intel5.webp";
import intel_55 from "../src/assets/intel/intel5.5.jpg";
import intel_6 from "../src/assets/intel/intel6.jpg";
import intel_7 from "../src/assets/intel/Intel7.jpg";
import intel_8 from "../src/assets/intel/intel8.jpg";
import intel_9 from "../src/assets/intel/intel9.jpg";

import game_dev_gif from "../src/assets/gamedev/game_dev.gif";
import game_dev1 from "../src/assets/gamedev/game_dev7.gif";
import game_dev2 from "../src/assets/gamedev/gamedev_3.gif";
import game_dev3 from "../src/assets/gamedev/gamedev_4.gif";

import stream_chat from "../src/assets/streamerchat/desktop.png";

import parlay_image from "../src/assets/parlaybot/parlay1.png";
import parlay_image2 from "../src/assets/parlaybot/parlay2.png";
import parlay_image3 from "../src/assets/parlaybot/parlay3.png";
import parlay_image4 from "../src/assets/parlaybot/parlay4.png";
import parlay_image5 from "../src/assets/parlaybot/parlay5.png";
import parlay_image6 from "../src/assets/parlaybot/parlay6.png";

import ohNo_image from "../src/assets/ohnotv/Ohno.png";
import { styled } from "@mui/system";
import ParticleComponent from "./ParticleComponent.js";
import FOG from "vanta/dist/vanta.fog.min.js";

const StyledDescription = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  "& p": {
    marginBottom: theme.spacing(2),
  },
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const ProjectDescription = ({ description }) => {
  // Split the description into paragraphs
  const paragraphs = description.split("\n\n");

  return (
    <StyledDescription>
      <HighlightText variant="h6">Project Overview</HighlightText>
      {paragraphs.map((paragraph, index) => (
        <React.Fragment key={index}>
          <Typography variant="body1" paragraph>
            {paragraph}
          </Typography>
          {index < paragraphs.length - 1 && <Divider sx={{ my: 2 }} />}
        </React.Fragment>
      ))}
    </StyledDescription>
  );
};

const isGif = (src) =>
  typeof src === "string" && src.toLowerCase().endsWith(".gif");

const CyclingCardMedia = ({ project }) => {
  const allImages = project.works
    ? project.works.flatMap((w) => w.images)
    : project.images;
  const shouldCycle = !isGif(project.image) && allImages.length > 1;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!shouldCycle) return;
    const interval = setInterval(() => {
      setIdx((prev) => (prev + 1) % allImages.length);
    }, 1500);
    return () => clearInterval(interval);
  }, [shouldCycle, allImages.length]);

  return (
    <CardMedia
      component="img"
      height="200"
      image={shouldCycle ? allImages[idx] : project.image}
      alt={project.title}
    />
  );
};

// Category-based muted palette
const techCategory = {
  // Frontend
  React: "frontend",
  "Material-UI": "frontend",
  JavaScript: "frontend",
  HTML: "frontend",
  CSS: "frontend",
  jQuery: "frontend",
  CoffeeScript: "frontend",
  Recharts: "frontend",
  Materialize: "frontend",
  HTML5: "frontend",

  // Backend
  "Ruby on Rails": "backend",
  Python: "backend",
  Flask: "backend",
  "Node.js": "backend",
  MySQL: "backend",
  PostgreSQL: "backend",
  "REST APIs": "backend",
  "RESTful APIs": "backend",
  WebSockets: "backend",
  HAML: "backend",

  // DevOps & Tools
  Git: "devops",
  Heroku: "devops",
  JIRA: "devops",
  "Agile/Scrum": "devops",
  Linux: "devops",
  "Server Hardware Repair": "devops",
  "Motherboard Replacement": "devops",
  "Fiber Connection Management": "devops",

  // Testing
  RSpec: "devops",

  // 3D & Graphics
  "Three.JS": "creative",
  WebGL: "creative",
  Maya: "creative",
  Photoshop: "creative",
  Blender: "creative",

  // Game Development
  Unity: "gamedev",
  "C#": "gamedev",
  "Unreal Engine": "gamedev",
  Blueprints: "gamedev",
  Metahumans: "gamedev",
  "Behavior Trees": "gamedev",
  "Real-time motion capture": "gamedev",
  "AI programming": "gamedev",
  "2D game development": "gamedev",
  "Multiplayer networking": "gamedev",
  "Game scripting": "gamedev",

  // Cloud & Services
  "AWS Polly": "cloud",

  // Blockchain
  Solidity: "blockchain",
  Blockchain: "blockchain",
  NFT: "blockchain",
  Crypto: "blockchain",

  // AI & Machine Learning
  ChatGPT: "ai",
  "Machine Learning": "ai",

  // Video Production
  "After Effects": "creative",
  "Adobe Premiere": "creative",

  // APIs
  "Rapid APIs": "backend",
  SportsRadar: "backend",
};

const categoryColors = {
  frontend: { bg: "rgba(96, 165, 250, 0.10)", text: "#3b82f6", border: "rgba(96, 165, 250, 0.25)" },
  backend: { bg: "rgba(52, 211, 153, 0.10)", text: "#10b981", border: "rgba(52, 211, 153, 0.25)" },
  devops: { bg: "rgba(251, 191, 36, 0.10)", text: "#b45309", border: "rgba(251, 191, 36, 0.25)" },
  creative: { bg: "rgba(192, 132, 252, 0.10)", text: "#8b5cf6", border: "rgba(192, 132, 252, 0.25)" },
  gamedev: { bg: "rgba(244, 114, 182, 0.10)", text: "#db2777", border: "rgba(244, 114, 182, 0.25)" },
  cloud: { bg: "rgba(251, 146, 60, 0.10)", text: "#c2410c", border: "rgba(251, 146, 60, 0.25)" },
  blockchain: { bg: "rgba(129, 140, 248, 0.10)", text: "#6366f1", border: "rgba(129, 140, 248, 0.25)" },
  ai: { bg: "rgba(45, 212, 191, 0.10)", text: "#0d9488", border: "rgba(45, 212, 191, 0.25)" },
  default: { bg: "rgba(148, 163, 184, 0.10)", text: "#64748b", border: "rgba(148, 163, 184, 0.25)" },
};

const TechStack = ({ tech }) => {
  const cat = techCategory[tech] || "default";
  const colors = categoryColors[cat];
  return (
    <Chip
      label={tech}
      size="small"
      sx={{
        m: "2px",
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        fontWeight: 500,
        fontSize: "0.75rem",
        height: "26px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const workProjects = [
  {
    title: "NYSHEX",
    description:
      "Developed interactive tools for the shipping industry using React, handling nested data, time series data, and designing robust endpoints. Engineered dynamic tables, drawers, and calendars to manage complex shipping data, ensuring seamless data integration and user-friendly interfaces.",
    image: nyshex_image,
    images: [nyshex_image, nyshex_image2, nyshex_image3, nyshex_image4],
    detailsImage: nyshex_image,
    detailedDescription:
      "At NYSHEX, I spent over two years engineering dynamic tables, drawers, and calendars specifically designed to handle nested information and time series data for the shipping industry. Using React and Material-UI, I developed scalable software solutions that addressed complex challenges within the Non-Vessel Operating Common Carrier (NVOCC) space. My role involved focusing on data mapping and resolving data integration issues to ensure that our software could seamlessly interact with various data sources and provide reliable insights.\n\nI collaborated closely with product managers, UX/UI designers, and backend developers to deliver high-quality, responsive, and user-friendly frontend solutions. This collaboration emphasized the importance of user experience and effective communication, ensuring that our tools met the specific needs of our users within the shipping supply chain.\n\nOne of my key achievements was the development of robust endpoints and interactive components that significantly enhanced the digital infrastructure of our clients' business operations. The dashboards and visualizations I engineered played a critical role in streamlining operations and providing actionable insights, thereby contributing to the overall efficiency and effectiveness of the shipping supply chain.",
    techStack: [
      "React",
      "Material-UI",
      "JavaScript",
      "HTML",
      "CSS",
      "REST APIs",
      "Node.js",
      "JIRA",
      "Agile/Scrum",
    ],
    video: null,
  },

  {
    title: "Action Face",
    description:
      "Built a 3D customizer using Ruby on Rails and Three.JS, allowing users to create and customize 3D face scans and miniatures directly in the browser. Developed new features and enhancements from UI/UX design documents and collaborated with QA to ensure high application quality and performance.",
    image: actionface_image,
    images: [actionface_image, actionface_image2],
    video: [
      "https://www.youtube.com/embed/3Vy993Ktxj8",
      "https://www.youtube.com/embed/7Kmgjt_Invc",
    ],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "At Action Face, I was instrumental in building a 3D customizer using Ruby on Rails and Three.JS, which allowed users to create and customize 3D face scans and miniatures directly in the browser. I developed new features and enhancements based on UI/UX design documents, utilizing Ruby on Rails, HAML, and JavaScript. My collaboration with QA and team members was key in resolving bugs and addressing user requests, ensuring the application maintained high quality and performance.\n\nOne of my notable contributions was creating dynamic shaders for 3D object customization using Three.JS. This feature significantly enhanced the visual effects and overall user experience for customizing face scans from mobile devices. The project not only showcased my technical skills but also demonstrated my ability to drive company growth through technological innovation, evidenced by Action Face's recent acquisition.",
    techStack: [
      "Ruby on Rails",
      "Three.JS",
      "HAML",
      "JavaScript",
      "HTML",
      "CSS",
      "WebGL",
      "Heroku",
      "PostgreSQL",
      "RSpec",
      "Git",
    ],
  },

  {
    title: "Intel",
    description:
      "Managed a large volumetric capture system for live sports and studio events, ensuring high-quality results under pressure. Worked with highly technical systems, collaborated across teams, and mentored new members.",
    image: intel_gif,
    images: [intel_gif3, intel_gif2, intel_gif4, intel_gif5, intel_55, intel_6, intel_7, intel_8, intel_9],
    video: [
      "https://www.youtube.com/embed/J7xIBoPr83A?si=Ojr9UK3Wwjsds4i7",
      "https://www.youtube.com/embed/6q-CEN7-XCk?si=cTtbb6h1O-aDwM4I",
      "https://www.youtube.com/embed/17_qunhzssA",
      "https://www.youtube.com/embed/PVEbBla-3cY",
      "https://www.youtube.com/embed/zu6F4XR9IeI",
    ],
    detailsImage: intel_gif,
    detailedDescription:
      "My role at Intel as a Systems Engineer involved managing a large volumetric capture system for live sports and studio events. This honed my ability to handle complex systems and deliver high-quality results under pressure, providing a solid foundation for collaborative and team-working skills. At Intel, I worked with highly technical systems, collaborated with many outside the technical space, mentored new team members, and built software to improve operations.\n\nI managed system server processes and hardware maintenance to deliver interactive volumetric content to Intel Sports and Intel Studios Business Partners. As a technical lead, I provided support and troubleshooting to maintain fully operational availability. I also engineered and designed system tools to assist in automating system processes, improving efficiency and reliability. Additionally, I trained new team members on system operation procedures, tools, and troubleshooting steps, ensuring they were well-equipped to handle the complex systems.\n\nThis role required a deep understanding of both hardware and software systems, and my contributions were crucial in maintaining the high performance and reliability expected by Intel's partners.",
    techStack: [
      "Python",
      "Linux",
      "Maya",
      "Server Hardware Repair",
      "Motherboard Replacement",
      "Fiber Connection Management",
    ],
  },
  {
    title: "Contracts.org",
    description:
      "Developed a comprehensive digital signature application for creating, signing, and sending contracts. Implemented features such as multiple signatures, drag-and-drop signature placement, and invite codes for service discounts.",
    image: contract_gif,
    images: [contract_gif2, contract_gif3, contract_gif4, contract_gif5],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "Contracts.org is a full-featured digital signature application that I built solo as a full stack project. The platform enables users to create, sign, and send contracts effortlessly. Users can choose from a variety of templates, allowing for a quick start with standardized contract formats. One of the standout features is the ability to capture multiple digital signatures, which ensures that all parties involved can sign the document electronically.\n\nTo enhance user experience, I implemented a drag-and-drop functionality for signature boxes, making it easy to indicate where signatures are required within the document. This intuitive interface streamlines the signing process and minimizes errors. Additionally, the application supports invite codes, offering users discounts on the service, which can be easily managed through the dashboard.\n\nThe dashboard itself provides a clear view of the contract's status, including invite codes and the various stages of contract completion. I significantly improved the contract generation time by approximately 85% through a meticulous refactoring of the system architecture and RESTful APIs. This optimization greatly enhanced the overall performance of the application.\n\nA key feature of the platform is the digital signature capture, which I implemented using an HTML5 canvas element combined with CoffeeScript and jQuery. This feature allows users to sign documents directly in the browser, ensuring a seamless and secure signing process. Adhering to test-driven development (TDD) principles, I wrote over 100 unit tests for user authentication to ensure a robust and reliable authentication system.\n\nThis project demonstrates my ability to deliver a complete, user-friendly, and high-performance application, leveraging a wide range of technologies to meet complex requirements.",
    techStack: [
      "Ruby on Rails",
      "HTML5",
      "CoffeeScript",
      "jQuery",
      "JavaScript",
      "CSS",
      "RSpec",
      "Git",
      "PostgreSQL",
      "RESTful APIs",
      "Heroku",
    ],
    video: null,
  },
];

const personalProjects = [
  {
    title: "Game Dev",
    description:
      "From a narrative stealth game built in Unreal Engine to a 2D multiplayer brawler in Unity with real-life motion capture sprites — game development across engines and genres.",
    image: "/hello/gamecap1.png",
    images: ["/hello/gamecap1.png"],
    detailsImage: "/hello/gamecap1.png",
    detailedDescription: "",
    techStack: [
      "Unreal Engine",
      "Blueprints",
      "Blender",
      "Metahumans",
      "Behavior Trees",
      "Unity",
      "C#",
    ],
    video: null,
    works: [
      {
        label: "HellO",
        images: [
          "/hello/gamecap1.png",
          "/hello/gamecap2.png",
          "/hello/gamecap3.png",
          "/hello/filecab.png",
          "/hello/NPCs.png",
          "/hello/activitypoinra.png",
          "/hello/collisions.png",
          "/hello/npcsActivities.png",
          "/hello/npcspatrol.png",
          "/hello/questVolumes.png",
        ],
        videos: [
          { src: "https://www.youtube.com/embed/8uRiA8mszfE", label: "GameWork", type: "youtube" },
          { src: "https://www.youtube.com/embed/sE_Az6VwMes", label: "Gameplay", type: "youtube" },
          { src: "https://www.youtube.com/embed/BsLIJnfo1_k", label: "Gameplay 2", type: "youtube" },
        ],
        description:
          "HellO is a narrative stealth/exploration game set in a modern corporate office building with many floors. The player explores the building, talking to workers, avoiding security during off hours, interacting with offices, desks and playing pranks on coworkers to progress higher up in the company.\n\nAs the story progresses, the player makes choices that affect a corruption/trust system, branching the narrative towards a moral or corrupt ending. The secret plot: the HellO company is opening a Hole to Hell by creating content that can demonically possess the public.\n\nBuilt in Unreal Engine with Blueprints, featuring a full quest and dialogue system with branching responses, NPC daily schedules and break routines, guard detection with AI perception sight cones and suspicion zones, a 3-story building with level streaming, inventory system, combat with lock-on and parry mechanics, stealth with crouching/crawling/strangling, and an elevator floor-select system. NPCs use Behavior Trees for patrol routes, break activities, and investigation behaviors.",
        techStack: [
          "Unreal Engine",
          "Blueprints",
          "Blender",
          "Metahumans",
          "Behavior Trees",
        ],
      },
      {
        label: "2D Brawler",
        images: [game_dev_gif, game_dev1, game_dev2, game_dev3],
        video: null,
        youtubeVideo: "https://www.youtube.com/embed/alyUmoao0ZQ?si=NjW6Nlqe2txvQMVB",
        description:
          "A 2D multiplayer game that captures real-life movements of streamers and turns them into sprite animations. Using Unity, I built the world and implemented game logic including AI search and destroy, 2D world rotation, and multiplayer view.\n\nKey features include capturing real people on film and converting their movements into sprite sheets, hit boxes for short and long-range attacks, AI behaviors, and a points system where players earn points by reaching specific zones.",
        techStack: [
          "Unity",
          "C#",
          "Photoshop",
          "Real-time motion capture",
          "AI programming",
          "Multiplayer networking",
        ],
      },
    ],
  },
  {
    title: "Parlay.bot",
    description:
      "Developed a web app for visualizing player stats across NFL, NBA, and MLB, enhanced with ChatGPT for creating parlays and machine learning models for performance projections. Users can analyze performance metrics through interactive charts and detailed game play-by-play analysis.",
    image: parlay_image2,
    images: [
      parlay_image2,
      parlay_image3,
      parlay_image4,
      parlay_image5,
      parlay_image6,
      parlay_image,
    ],
    video: ["https://www.youtube.com/embed/ZvB4DAyi8KQ?si=6f2DHvWJ8vjPFD6J"],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "Parlay.bot is a comprehensive web application designed to visualize player stats across NFL, NBA, and MLB. The platform provides users with the ability to view and analyze performance metrics through interactive and dynamic charts. As the sole developer of this project, I created responsive interfaces using React and integrated data visualizations with Recharts, ensuring a seamless and engaging user experience.\n\nThe backend was built with Python and Flask, leveraging MySQL for robust data management. I integrated various APIs, including Rapid APIs and SportsRadar, to fetch and display up-to-date player statistics. The application also features user authentication, allowing users to filter and personalize their data views securely.\n\nA key feature of Parlay.bot is the integration of ChatGPT as an assistant for creating parlays for game events. Additionally, machine learning models analyze player performance, providing users with projections of players who are expected to perform better or worse than the betting line. This advanced functionality enhances the user's ability to make informed betting decisions.\n\nUsers can go back to each game play-by-play, see detailed charts, lines, and review past and upcoming games. This comprehensive analysis tool supports users in making data-driven decisions for their parlays.\n\nKey highlights of the project include:\n- Created responsive and intuitive user interfaces with React.\n- Integrated dynamic data visualizations using Recharts, providing users with clear and insightful performance metrics.\n- Implemented robust user authentication and data filtering features, ensuring secure and personalized user experiences.\n- Integrated ChatGPT for creating parlays and providing betting assistance.\n- Developed machine learning models to project player performance relative to betting lines.\n- Enabled detailed game play-by-play analysis, chart views, and comprehensive game data review.\n- Optimized performance and ensured efficient data handling, resulting in a smooth and reliable application.\n\nThis project showcases my ability to develop full stack web applications, integrate complex data visualizations, leverage AI and machine learning models, and ensure high performance and security.",
    techStack: [
      "React",
      "Recharts",
      "Materialize",
      "Python",
      "Flask",
      "MySQL",
      "Rapid APIs",
      "SportsRadar",
      "ChatGPT",
      "Machine Learning",
      "JavaScript",
      "CSS",
      "HTML",
      "Git",
      "Heroku",
    ],
  },

  {
    title: "OhNo.tv",
    description:
      "Developed a streaming web app with blockchain integration for donations via NFT or crypto. OhNo.tv features a main channel with a queue system, personal chat rooms, and unique chat functionalities.",
    image: ohNo_image,
    images: [ohNo_image],
    video: [
      "https://www.youtube.com/embed/32F6a1H3Uqg?si=i4BYR0MZ07-RXLSe",
      "https://www.youtube.com/embed/y_tBaaEEuWM?si=sgwAJo4mvQicB8lt",
      "https://www.youtube.com/embed/moVn8fg9Q6Q?si=kaipOLoEyWX1RTcZ",
      "https://www.youtube.com/embed/kS4WvnAfBr4?si=dKvhRujBF0bo_RwE",
    ],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "OhNo.tv, dubbed the 'Reddit of livestreaming,' is a comprehensive streaming web app that I developed as a Full-stack Engineer from November 2021 to February 2022. The platform integrates blockchain technology for donations via NFT or crypto, providing a secure and innovative way for streamers to receive support from their audience.\n\nThe application features a main channel with a queue system that determines homepage presence. Viewers can vote to keep the main streamer featured, creating an interactive and dynamic user experience. Each personal chat room supports group chat, queues, or single-person streaming, enhancing the community aspect of the platform.\n\nI developed OhNo.tv using a diverse technology stack, including JavaScript, jQuery, and HTML5 for the frontend, and Ruby on Rails, WebSockets, Solidity, and PostgreSQL for the backend. The integration of WebSockets enabled real-time communication, while Solidity was used to implement blockchain features for secure and efficient handling of crypto transactions and NFT donations.\n\nAdditionally, I implemented a queue system to manage stream appearances on the homepage, ensuring a fair and engaging process for streamers and viewers alike. The platform also includes video ads and campaign functionality, capturing the reach and audience effectively.\n\nKey highlights of the project include:\n- Built a full-stack application using Ruby on Rails and WebSockets.\n- Integrated blockchain features for donations using Solidity.\n- Implemented a queue system to manage stream appearances on the homepage.\n- Ensured secure and efficient handling of crypto transactions and NFT donations.\n- Developed unique chat functionalities, including group chat and queue lineups.\n- Included video ads and campaign functionality to capture reach and audience.\n\nOhNo.tv showcases my ability to develop innovative streaming solutions, leverage blockchain technology, and create engaging user experiences.",
    techStack: [
      "JavaScript",
      "jQuery",
      "HTML5",
      "Ruby on Rails",
      "WebSockets",
      "Solidity",
      "PostgreSQL",
      "Blockchain",
      "NFT",
      "Crypto",
      "Git",
      "Heroku",
    ],
  },

  {
    title: "SpeechSynth StreamChat",
    description:
      "Developed a web app that allows streamers to assign personalized voices to chatters watching their live streams, enhancing viewer interaction with real-time voice customization and video recommendations.",
    image: stream_chat,
    images: [stream_chat],
    video: ["https://www.youtube.com/embed/zxirSB30W4s?si=cXGN9RxdF2glp0QU"],
    detailsImage: "https://via.placeholder.com/600x400",
    detailedDescription:
      "SpeechSynth StreamChat is an innovative web application that enhances viewer interaction by allowing streamers to assign personalized voices to chatters during their live streams. This project leverages AWS Polly to provide a variety of voices, giving viewers the ability to type commands and change their voice in real-time. This functionality adds a unique and engaging layer to the streaming experience.\n\nThe application supports integration with popular streaming platforms like Twitch and YouTube. Chatters can also recommend YouTube videos to play through commands, further enhancing the interactive experience. This feature allows for a more dynamic and entertaining stream, as viewers can directly influence the content being played.\n\nI developed SpeechSynth StreamChat using a robust technology stack, ensuring a seamless and user-friendly interface. The frontend was built with React and Material-UI, providing a responsive and visually appealing design. The backend, developed with Node.js, handled the REST APIs and real-time data processing, ensuring smooth performance and scalability.\n\nKey highlights of the project include:\n- Integrated AWS Polly to offer a variety of personalized voices for chatters.\n- Enabled real-time voice customization through chat commands.\n- Supported video recommendations from chatters via YouTube commands.\n- Built a responsive and user-friendly interface using React and Material-UI.\n- Ensured smooth and scalable performance with Node.js and REST APIs.\n- Implemented robust data handling and real-time processing to maintain high-quality user interactions.\n\nSpeechSynth StreamChat demonstrates my capability to create user-centric, innovative solutions in the streaming space, enhancing viewer engagement and interaction.",
    techStack: [
      "React",
      "JavaScript",
      "HTML",
      "CSS",
      "REST APIs",
      "Node.js",
      "AWS Polly",
      "Git",
    ],
  },
];

const videoCategories = [
  {
    category: "Concerts",
    thumbnail: "/thumbnails/59541085.jpg",
    techStack: ["After Effects", "Adobe Premiere"],
    videos: [
      { title: "Baauer @ 1015 Folsom", id: "59541085" },
      { title: "I Love Makonnen @ 1015 Folsom", id: "115134421" },
      { title: "TRAP CITY!! 1 Year @ Temple", id: "71068144" },
      { title: "Trap City: Carnage 02/23/13", id: "60714424" },
      { title: 'Trap City "All Gold Everything Edition"', id: "58502685" },
      { title: "Trap City! Trap Trap City!", id: "56047004" },
      { title: "The Woom", id: "62133100" },
      { title: "FUTURE | PERFECT", id: "61234584" },
      { title: "XXYYXX, TEEBS, DJ DIALS, NANOSAUR", id: "60922103" },
      { title: '"The Gift" - Hosted by Jay Ant', id: "60609095" },
      { title: "The Gift @ Vessel! 2/24/13", id: "58592843" },
      { title: "The Gift @ Vessel ! 1/27/13", id: "57023607" },
      { title: "Kreayshawn @ 1015 Folsom !!!", id: "57568453" },
      { title: "TABOO @ DNA LOUNGE!", id: "56347143" },
      { title: "Trash Wang Tour!", id: "56130242" },
      { title: "TurntUp Presents The TraP!", id: "54495089" },
      { title: "The Internet live @ 330 Ritch: Give it Time!", id: "53972229" },
      {
        title: "Y3k: Kitty Pryde + Main Attrakionz + Hottub @ DNA LOUNGE",
        id: "53895732",
      },
      { title: "5 Point Palm live @ 111 Minna", id: "52428416" },
      { title: "5th Nebula Live @ 111 Minna", id: "52422074" },
      { title: "Trial & Error live @ 111 Minna : DUCKWRTH", id: "52031246" },
    ],
  },
  {
    category: "Interviews",
    thumbnail: "/thumbnails/60919009.jpg",
    techStack: ["After Effects", "Adobe Premiere"],
    videos: [
      { title: "An Interview with XXYYXX!", id: "60919009" },
      { title: "Justin Lovato... This Too Shall Pass", id: "27057595" },
      { title: "Ian Shults... The Social Contract", id: "30802200" },
      {
        title: 'Bobbie "Spider" Webb: Divisadero neighbor since 1945',
        id: "51555662",
      },
      { title: "Damon Soule - Then What Happened", id: "27791394" },
      { title: "Laugh & Draw with Pablo De' Pinho", id: "52177148" },
      { title: "Brett Amory ThrowBack", id: "52427801" },
      { title: "Visiting Susan Wu.", id: "52896272" },
    ],
  },
  {
    category: "Arts",
    thumbnail: "/thumbnails/60048739.jpg",
    techStack: ["After Effects", "Adobe Premiere"],
    videos: [
      { title: "HOW & NOSM SF MURAL", id: "60048739" },
      { title: "Sam Flores: Lion and the Lamb", id: "57973775" },
      { title: "SLVSTR RELEASE!", id: "53906075" },
      { title: "Mister x Infinite", id: "53048314" },
      { title: "Rise Japan Vol. 4", id: "30896598" },
      { title: "Find Art Magazine Pop Up", id: "26455681" },
    ],
  },
  {
    category: "Teasers",
    thumbnail: "/thumbnails/92216478.jpg",
    techStack: ["After Effects", "Adobe Premiere"],
    videos: [
      { title: "NYCHOS UnReleased Teaser..", id: "92216478" },
      { title: "Truth OutlookFestival Trailer", id: "184507050" },
      { title: 'Sav Noir "THE 1AM COLLECTION" Release', id: "58683846" },
      { title: "Ian Shults: The Social Contract - Trailer", id: "30562189" },
    ],
  },
];

const additionalProjects = [
  // {
  //   title: "Mockups",
  //   description: "Various design mockups for web and mobile applications.",
  //   image: cakepop_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about the mockups goes here...",
  //   techStack: [],
  // },
  // {
  //   title: "Internet of Things",
  //   description: "Projects involving IoT devices and automation.",
  //   image: iot_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about IoT projects goes here...",
  //   techStack: [],
  // },
  // {
  //   title: "3D Printing",
  //   description: "Various projects involving 3D printing technology.",
  //   image: printed_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about 3D printing projects goes here...",
  //   techStack: [],
  // },
  // {
  //   title: "Film",
  //   description: "Various projects involving 3D printing technology.",
  //   image: printed_gif,
  //   images: [
  //     nyshex_image,
  //     "https://via.placeholder.com/600x400/1",
  //     "https://via.placeholder.com/600x400/2",
  //     "https://via.placeholder.com/600x400/3",
  //   ],
  //   detailsImage: "https://via.placeholder.com/600x400",
  //   detailedDescription:
  //     "More detailed information about 3D printing projects goes here...",
  //   techStack: [],
  // },
];

const WorkTabContent = ({ work, isMobile, onImageClick }) => {
  // Build a unified media list: videos first, then images
  const allVideos = work.videos || (work.video ? [{ src: work.video, label: work.label }] : []);
  const mediaItems = [
    ...allVideos.map((v) => ({
      type: v.type === "youtube" ? "youtube" : "video",
      src: v.src,
      label: v.label,
    })),
    ...(work.youtubeVideo
      ? [{ type: "youtube", src: work.youtubeVideo, label: work.label }]
      : []),
    ...work.images.map((img) => ({ type: "image", src: img })),
  ];

  // Default to first item (first video)
  const [activeIndex, setActiveIndex] = useState(0);
  const active = mediaItems[activeIndex] || mediaItems[0];

  const handleThumbClick = (index) => {
    const item = mediaItems[index];
    setActiveIndex(index);
    // Images also open the modal
    if (item.type === "image") {
      onImageClick(item.src);
    }
  };

  return (
    <Box>
      {/* Main display area */}
      <Box sx={{ mb: 2 }}>
        {active.type === "video" && (
          <video
            key={active.src}
            src={active.src}
            controls
            style={{
              width: "100%",
              borderRadius: "4px",
              maxHeight: "400px",
            }}
          />
        )}
        {active.type === "youtube" && (
          <Box
            component="iframe"
            src={active.src}
            title={active.label}
            sx={{
              width: "100%",
              height: isMobile ? "250px" : "350px",
              border: 0,
              borderRadius: 1,
              display: "block",
            }}
            allowFullScreen
          />
        )}
        {active.type === "image" && (
          <img
            src={active.src}
            alt={work.label}
            onClick={() => onImageClick(active.src)}
            style={{
              width: "100%",
              maxHeight: "400px",
              objectFit: "cover",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
        )}
      </Box>

      {/* Thumbnail grid — videos + images together */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {mediaItems.map((item, j) => (
          <Grid item xs={3} key={j}>
            <Box
              onClick={() => handleThumbClick(j)}
              sx={{
                position: "relative",
                cursor: "pointer",
                borderRadius: 1,
                overflow: "hidden",
                border:
                  activeIndex === j
                    ? "2px solid #1976d2"
                    : "2px solid transparent",
                opacity: activeIndex === j ? 1 : 0.7,
                transition: "opacity 0.2s, border 0.2s",
                "&:hover": { opacity: 1 },
              }}
            >
              {item.type === "video" ? (
                <>
                  <video
                    src={item.src}
                    muted
                    style={{
                      width: "100%",
                      height: "80px",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0,0,0,0.3)",
                    }}
                  >
                    <Typography sx={{ color: "white", fontSize: "1.4rem" }}>
                      ▶
                    </Typography>
                  </Box>
                </>
              ) : item.type === "youtube" ? (
                <>
                  <img
                    src={ytThumb(item.src)}
                    alt={item.label || "Video"}
                    style={{
                      width: "100%",
                      height: "80px",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0,0,0,0.3)",
                    }}
                  >
                    <Typography sx={{ color: "red", fontSize: "1.4rem" }}>
                      ▶
                    </Typography>
                  </Box>
                </>
              ) : (
                <img
                  src={item.src}
                  alt={`${work.label} ${j + 1}`}
                  style={{
                    width: "100%",
                    height: "80px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      <ProjectDescription description={work.description} />
      <Typography variant="h6" gutterBottom>
        Tech Stack
      </Typography>
      <Box display="flex" flexWrap="wrap" mb={2}>
        {work.techStack.map((tech, j) => (
          <TechStack tech={tech} key={j} />
        ))}
      </Box>
    </Box>
  );
};

const ytThumb = (embedUrl) => {
  const match = embedUrl.match(/\/embed\/([^?/]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : "";
};

const toSlug = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const allProjects = [...workProjects, ...personalProjects];

const Work = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  const vantaRef = useRef(null);

  useEffect(() => {
    let vantaEffect = null;
    vantaEffect = FOG({
      el: vantaRef.current,
      THREE: THREE,
      mouseControls: true,
      touchControls: true,
      gyroControls: false,

      highlightColor: 0xf0b01,
      midtoneColor: 0x69d82,
      baseColor: 0x0,
      speed: 3,
    });

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, []);

  const [workTab, setWorkTab] = useState(0);
  const [modalImage, setModalImage] = useState(null);

  const handleOpenDrawer = useCallback((project) => {
    setSelectedProject(project);
    setWorkTab(0);
    setDrawerOpen(true);
    setSearchParams({ project: toSlug(project.title) });
  }, [setSearchParams]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedProject(null);
    setWorkTab(0);
    setSearchParams({});
  }, [setSearchParams]);

  const [videoDrawerOpen, setVideoDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);

  const handleOpenVideoDrawer = useCallback((category) => {
    setSelectedCategory(category);
    setActiveVideoId(category.videos[0].id);
    setVideoDrawerOpen(true);
    setSearchParams({ video: toSlug(category.category) });
  }, [setSearchParams]);

  const handleCloseVideoDrawer = useCallback(() => {
    setVideoDrawerOpen(false);
    setSelectedCategory(null);
    setActiveVideoId(null);
    setSearchParams({});
  }, [setSearchParams]);

  // Open drawer from URL on mount
  useEffect(() => {
    const projectSlug = searchParams.get("project");
    const videoSlug = searchParams.get("video");

    if (projectSlug) {
      const match = allProjects.find((p) => toSlug(p.title) === projectSlug);
      if (match) {
        setSelectedProject(match);
        setWorkTab(0);
        setDrawerOpen(true);
      }
    } else if (videoSlug) {
      const match = videoCategories.find(
        (c) => toSlug(c.category) === videoSlug
      );
      if (match) {
        setSelectedCategory(match);
        setActiveVideoId(match.videos[0].id);
        setVideoDrawerOpen(true);
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderProjects = (projects) =>
    projects.map((project, index) => (
      <Grid item xs={12} sm={6} md={6} lg={2} xl={3} key={index}>
        <Card
          style={{
            backgroundColor: "rgba(240, 240, 240, 0.3)",
            backdropFilter: "blur(10px)",

            color: "black",
          }}
          onClick={() => handleOpenDrawer(project)}
        >
          <CyclingCardMedia project={project} />
          <CardContent
            style={{
              backgroundColor: "rgba(240, 240, 240, 0.8)",
              backdropFilter: "blur(10px)",
              padding: "20px",
              color: "black",
            }}
          >
            <Typography variant="h5">{project.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {project.description}
            </Typography>
            <Box mt={2}>
              <Typography variant="subtitle1">Tech Stack:</Typography>
              <Box display="flex" flexWrap="wrap">
                {project.techStack.map((tech, index) => (
                  <TechStack tech={tech} />
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ));

  return (
    <>
      <div style={{ position: "absolute", top: 0, zIndex: 0 }}>
        <ParticleComponent width="50%" height="50%" />
      </div>
      <Box style={{ position: "relative" }} className="work-info">
        <Box ref={vantaRef} className="work-container">
          <Box>
            <Typography
              variant="h4"
              gutterBottom
              style={{
                color: "white",
              }}
            >
              Work
            </Typography>
            <Typography
              variant="h6"
              gutterBottom
              style={{
                color: "white",
                backgroundColor: "rgba(47, 79, 79, 0.8)",
                backdropFilter: "blur(10px)",
                borderRadius: "5px",
                padding: "10px",
                width: "fit-content",
              }}
            >
              Professional Experience
            </Typography>
            <Grid container spacing={4}>
              {renderProjects(workProjects)}
            </Grid>
            <Typography
              variant="h6"
              gutterBottom
              style={{
                marginTop: "20px",
                color: "white",
                backgroundColor: "rgba(47, 79, 79, 0.8)",
                backdropFilter: "blur(10px)",
                borderRadius: "5px",
                padding: "10px",
                width: "fit-content",
              }}
            >
              Projects
            </Typography>
            <Grid container spacing={4}>
              {renderProjects(personalProjects)}
            </Grid>

            <Typography
              variant="h6"
              gutterBottom
              style={{
                marginTop: "20px",
                color: "white",
                backgroundColor: "rgba(47, 79, 79, 0.8)",
                backdropFilter: "blur(10px)",
                borderRadius: "5px",
                padding: "10px",
                width: "fit-content",
              }}
            >
              Video Work
            </Typography>
            <Grid container spacing={4}>
              {videoCategories.map((cat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    style={{
                      backgroundColor: "rgba(240, 240, 240, 0.3)",
                      backdropFilter: "blur(10px)",
                      color: "black",
                      cursor: "pointer",
                    }}
                    onClick={() => handleOpenVideoDrawer(cat)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={cat.thumbnail}
                      alt={cat.category}
                    />
                    <CardContent
                      style={{
                        backgroundColor: "rgba(240, 240, 240, 0.8)",
                        backdropFilter: "blur(10px)",
                        padding: "20px",
                        color: "black",
                      }}
                    >
                      <Typography variant="h5">{cat.category}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {cat.videos.length} videos
                      </Typography>
                      <Box mt={2}>
                        <Box display="flex" flexWrap="wrap">
                          {cat.techStack.map((tech, i) => (
                            <TechStack tech={tech} key={i} />
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
        <Box>
          <Box>
            <Typography variant="h6" gutterBottom style={{ marginTop: "20px" }}>
              Additional Work
            </Typography>
            <Grid container spacing={4}>
              {renderProjects(additionalProjects)}
            </Grid>
          </Box>
        </Box>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          PaperProps={{
            sx: {
              width: isMobile ? "80%" : "50%",
            },
          }}
        >
          <Box sx={{ padding: 2 }} role="presentation">
            <IconButton
              onClick={handleCloseDrawer}
              sx={{ position: "fixed", top: 0, right: 0 }}
            >
              <CloseIcon />
            </IconButton>
            {selectedProject && !selectedProject.works && (
              <>
                <Typography variant="h5" gutterBottom>
                  {selectedProject.title}
                </Typography>
                <WorkTabContent
                  work={{
                    label: selectedProject.title,
                    images: selectedProject.images,
                    videos: selectedProject.video
                      ? selectedProject.video.map((url, i) => ({
                          src: url,
                          label: `Video ${i + 1}`,
                          type: "youtube",
                        }))
                      : [],
                    description: selectedProject.detailedDescription,
                    techStack: selectedProject.techStack,
                  }}
                  isMobile={isMobile}
                  onImageClick={setModalImage}
                />
              </>
            )}
            {selectedProject && selectedProject.works && (
              <>
                <Typography variant="h5" gutterBottom>
                  {selectedProject.title}
                </Typography>
                <Tabs
                  value={workTab}
                  onChange={(e, v) => setWorkTab(v)}
                  sx={{ mb: 2 }}
                >
                  {selectedProject.works.map((work, i) => (
                    <Tab label={work.label} key={i} />
                  ))}
                </Tabs>
                {selectedProject.works.map((work, i) =>
                  workTab === i ? (
                    <WorkTabContent
                      key={i}
                      work={work}
                      isMobile={isMobile}
                      onImageClick={setModalImage}
                    />
                  ) : null
                )}
              </>
            )}
          </Box>
        </Drawer>

        <Drawer
          anchor="right"
          open={videoDrawerOpen}
          onClose={handleCloseVideoDrawer}
          PaperProps={{
            sx: {
              width: isMobile ? "90%" : "50%",
            },
          }}
        >
          <Box sx={{ p: 0 }} role="presentation">
            <IconButton
              onClick={handleCloseVideoDrawer}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: "rgba(0,0,0,0.5)",
                color: "white",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            {selectedCategory && (
              <>
                <Box
                  component="iframe"
                  src={`https://player.vimeo.com/video/${activeVideoId}`}
                  title={
                    selectedCategory.videos.find((v) => v.id === activeVideoId)
                      ?.title
                  }
                  sx={{
                    width: "100%",
                    height: isMobile ? "250px" : "400px",
                    border: 0,
                    display: "block",
                  }}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {
                      selectedCategory.videos.find(
                        (v) => v.id === activeVideoId
                      )?.title
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCategory.category}
                  </Typography>
                </Box>
                <Divider sx={{ mx: 2, my: 1.5 }} />
                <Box sx={{ px: 2, pb: 2 }}>
                  <Grid container spacing={1}>
                    {selectedCategory.videos.map((video) => (
                      <Grid item xs={4} sm={3} key={video.id}>
                        <Box
                          onClick={() => setActiveVideoId(video.id)}
                          sx={{
                            cursor: "pointer",
                            borderRadius: 1,
                            overflow: "hidden",
                            border:
                              activeVideoId === video.id
                                ? "2px solid #1976d2"
                                : "2px solid transparent",
                            opacity: activeVideoId === video.id ? 1 : 0.7,
                            transition: "opacity 0.2s, border 0.2s",
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <img
                            src={`/thumbnails/${video.id}.jpg`}
                            alt={video.title}
                            style={{
                              width: "100%",
                              height: "80px",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              p: 0.5,
                              lineHeight: 1.2,
                              fontSize: "0.65rem",
                            }}
                            noWrap
                          >
                            {video.title}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        </Drawer>

        <Modal open={!!modalImage} onClose={() => setModalImage(null)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            <IconButton
              onClick={() => setModalImage(null)}
              sx={{
                position: "absolute",
                top: -40,
                right: 0,
                color: "white",
              }}
            >
              <CloseIcon />
            </IconButton>
            {modalImage && (
              <img
                src={modalImage}
                alt="Full size"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "85vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            )}
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default Work;
